
import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, IconButton } from "@mui/material";
import ResetIcon from "@mui/icons-material/RotateLeft";
import MuiTooltip from "@mui/material/Tooltip";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";

import SplitterContainer from "@plannel/components/SplitterContainer";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import CodeUtil from "@plannel/pages/utils/code-util";
import PaginationContainer from "@plannel/components/PaginationContainer";
import { isEmpty, isNil, replace, size, cloneDeep } from "lodash";
import expiredInventoryService from "@plannel/services/rp/expired-inventory-service";
import { format } from "date-fns";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import NumberUtil from "@plannel/utils/number-util";

const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode } = CodeUtil;
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;
const ExpiredInventory = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [refreshToggle, setRefreshToggle] = useState(false);

  const pageSizes = [30, 50, 100];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(1);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [treeload, setTreeload] = useState(false);
  const selectChartDate = useRef(null);

  const [rpExpInventoryGridData, setRpExpInventoryGridData] = useState([]);
  const [rpExpInventoryChartData, setRpExpInventoryChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const userData = JSON.parse(localStorage.getItem("user"));

  const currencyMap = useRef({});
  const EXCEL_EXPORT = "excelExport";

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const gridRef = useRef();
  const chartRawRef = useRef(null); // 마지막 차트 응답 — 언어 변경 시 재조립용

  const chartSeries = [
    { field: "totalInvQty", text: t("totalInvQty"), type: "bar", orientation: "left", groupName: t("qty")},
    { field: "totalInvAmt", text: t("totalInvAmt"), type: "bar", orientation: "left", groupName: t("amt")},
    { field: "expectedExpQty", text: t("expQty"), type: "line", orientation: "right", groupName: t("expQty")},
    { field: "expectedExpAmt", text: t("expAmt"), type: "line", orientation: "right", groupName: t("expAmt")}
  ]

  const defaultSeries = [chartSeries[0], chartSeries[2]];

  const pivotConfig = {
    groupKey: "measure",
    chartSeries,
    include: chartLabel
  }

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.locationCd;

    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }

    const value = param.data?.[param?.colDef?.field] ?? 0;
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;


  const columnDefs = [
    { headerName: "locDesc", field: "locationDesc", width: 150, type: ["nonEditableColumn"], hide: true },
    { headerName: "locCd", field: "locationCd", width: 150, type: ["nonEditableColumn"], cellClass: 'stringType' },
    { headerName: "locName", field: "locationName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "itemCd", field: "itemCd", width: 150, type: ["nonEditableColumn"], cellClass: 'stringType' },
    { headerName: "itemName", field: "itemName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "itemHrchy", field: "itemHrchyName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "invQty", field: "totalInvQty", width: 160, type: ["nonEditableColumn", "rightAligned", "numberColumn"] },
    { headerName: "invAmt", field: "totalInvAmt", width: 170, type: ["nonEditableColumn", "rightAligned", "numberColumn"], valueFormatter: currencyRenderer },
    { headerName: "expDate", field: "baseDate", width: 150, type: ["nonEditableColumn"] },
    { headerName: "expQty", field: "expInvQty", width: 150, type: ["nonEditableColumn", "rightAligned", "numberColumn"] },
    { headerName: "expAmt", field: "expInvAmt", width: 150, type: ["nonEditableColumn", "rightAligned", "numberColumn"], valueFormatter: currencyRenderer  },
  ]

  const getChartRequestParams = () => {
    const params = {};

    if (!isEmpty(rpVersionFilter)) {
      params.versionId = rpVersionFilter.id;
    }

    if (filterValue?.category) {
      params.itemHrchyCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }
    return params;
  }

  /* Back 에서 가져온 데이터를 사용가능한 Chart Data 변경해줌. */
  const processChartResults = (localDateHeader, chartData) => {
    chartRawRef.current = { localDateHeader, chartData };

    const totalInvQtyMap = {}
    const totalInvAmtMap = {}
    const expectedExpQtyMap = {}
    const expectedExpAmtMap = {}
    const dateFormat = [];

    for (let i = 0; i < localDateHeader.length; i++) {
      const baseDate = format(localDateHeader[i], 'yyyy-MM');
      dateFormat.push(baseDate);
      totalInvQtyMap[baseDate] = chartData?.totalInvQtyPivotData[i];
      totalInvAmtMap[baseDate] = chartData?.totalInvAmtPivotData[i];
      expectedExpQtyMap[baseDate] = chartData?.expectedExpQtyPivotData[i];
      expectedExpAmtMap[baseDate] = chartData?.expectedExpAmtPivotData[i];
    }
  

    const processedData = [
      { measure: t("totalInvQty"), ...totalInvQtyMap },
      { measure: t("totalInvAmt"), ...totalInvAmtMap },
      { measure: t("expQty"), ...expectedExpQtyMap },
      { measure: t("expAmt"), ...expectedExpAmtMap }
    ]

    setChartLabel(dateFormat);
    return processedData;
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    const raw = chartRawRef.current;
    if (isNil(raw)) {
      return;
    }
    setRpExpInventoryChartData(processChartResults(raw.localDateHeader, raw.chartData));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const retrieveRpExpireInventoryChart = () => {
    const params = getChartRequestParams();
    setTreeload(false);

    expiredInventoryService.getChartData(params)
    .then(response => {
      if (response?.data?.pivotResult) {
        const { localDateHeader, data } = response?.data?.pivotResult;
        const chartData = processChartResults(localDateHeader, data[0]);

        setRpExpInventoryChartData(chartData);
      } else {
        setRpExpInventoryChartData([]);
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
      }
    })
    .catch(error => {
      debug(error);
      setRpExpInventoryChartData([]);
      setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
    })
    .then(() => {
      const reduxState = reduxUtil.createObjectState(viewName, { rpVersion: rpVersionFilter, filter: filterValue, currentPage, pageSize });

      reduxDispatch(reduxUtil.updateViewState(reduxState));
    })
  }

  const getGridRequestParams = (init, pagination = true) => {
    const params = {};

    if (!isEmpty(rpVersionFilter)) {
      params.versionId = rpVersionFilter.id;
    }

    /* Refresh(Reset) 버튼을 누르는경우 초기화 하기 위함.. */
    if (isNil(init) || !init) {
      if (filterValue?.category) {
        params.itemHrchyCd = filterValue.category.hrchyCd;
      }

      if (filterValue?.locations) {
        params.locationCd = filterValue?.locations?.map(location => location?.locCd);
      }

      if (selectChartDate.current !== null && !isEmpty(selectChartDate.current)) {
        const baseDate = new ZDate(`${selectChartDate.current}-01`);
        const startDate = dateUtils.startOfMonthDate(baseDate);
        const endDate = dateUtils.endOfMonthDate(baseDate);

        params.startDate = startDate;
        params.endDate = endDate;
      }
    }

    /* Excel Export시 사용. */
    if (pagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    return params;
  }

  const retrieveRpExpireInventoryGrid = (init = false) => {
    const params = getGridRequestParams(init);
    
    expiredInventoryService.getGridData(params)
    .then(response => {
      if (response?.data?.results && response?.data?.results.length > 0) {
        const { results, totalPages, currency } = response.data;

        setRpExpInventoryGridData(results);
        setGridPages(totalPages);
        if (size(currency)) {
          currencyMap.current = currency;
        }

      } else {
        setRpExpInventoryGridData([]);
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: `${t("expQty")} ${t("MSG_NoData")}` });
      }
    })
    .catch(error => {
      debug(error);
      setRpExpInventoryGridData([]);
      setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
    });
  }

  const chartClickEvent = (params) => {
    const { activeLabel } = params;
    selectChartDate.current = activeLabel;

    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveRpExpireInventoryGrid();
    }
  }

  const refreshPage = () => {
    setRefreshToggle(true);

    setFilterValue({});
    setTreeload(true);
    selectChartDate.current = null;
    retrieveRpExpireInventoryGrid(true); // 초기화만 (init = true, pagination = false)

    setRefreshToggle(false);
  }

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setVisibleColumns(cloneDeep(GridUtils.getVisibleColumns(gridRef)));
  }

  const onColumnVisible = () => {
    const updatedVisibleColumns = GridUtils.getVisibleColumns(gridRef);
    setVisibleColumns(cloneDeep(updatedVisibleColumns));
  }

  const retrieveExportColumns = () => {
    if (visibleColumns.isEmpty) {
      return columnDefs;
    }
     
    return visibleColumns
  }

  useEffect(() => {
    setCurrentPage(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize])

  useEffect(() => {
    if (!isEmpty(rpVersionFilter) && !refreshToggle) {
      retrieveRpExpireInventoryGrid();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  useEffect(() => {
    if (!isEmpty(rpVersionFilter) && !refreshToggle) {
      retrieveRpExpireInventoryChart();
      retrieveRpExpireInventoryGrid();
    }

    /* Search Filter 변경시 Grid부분 초기화. */
    selectChartDate.current = null

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter, filterValue?.category, filterValue?.locations]);

  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} treeload={treeload} enableMultiSelect selectLeafs/>
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <MuiTooltip title="Reset">
          <IconButton size="small" onClick={refreshPage}>
            <ResetIcon />
          </IconButton>
        </MuiTooltip>
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={expiredInventoryService} columnDefs={retrieveExportColumns()} params={getGridRequestParams(false, false)} />
        </Box>
      </FilterContainer>
      <Box height={"100%"}>
        <SplitterContainer initialSizes={[1, 1]} collapseDirection="up">
          <Box className={"chart-area"}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={rpExpInventoryChartData}
              chartClickEvent={chartClickEvent}
              defaultSeries={defaultSeries}
              nullConvert
            />
          </Box>
          <Box sx={{ height: "calc(100% - 20px)", mt: 2 }}>
            <Box className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 40px)", mb: 1 }}>
              <AgGridReact
                ref={gridRef}
                rowData={rpExpInventoryGridData}
                onColumnVisible={onColumnVisible}
                onColumnMoved={onColumnVisible}
                {...defaultGridMemo}
                onGridReady={onGridReady}
              />
            </Box>
            <PaginationContainer
              currentPage={currentPage} totalPages={gridPages}
              pageSize={pageSize} pageSizes={pageSizes}
              setCurrentPage={setCurrentPage} setPageSize={setPageSize}
            />
          </Box>
        </SplitterContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
}

export default withTranslation()(ExpiredInventory);